import React, { useReducer, useContext, useCallback, useMemo } from 'react';
import { M3uMedia } from "../../lib/m3u";
import { ChannelContextType, initialChannelContextType } from "./types";
import { selectedChannelReducer, initialChannelState } from "./SelectedChannelReducer";
import { selectChannel, removeSelectedChannel } from "./actions";
import { ChannelHistoryContext } from "./ChannelHistoryContext";

export const SelectedChannelContext = React.createContext<ChannelContextType>(initialChannelContextType);

const SelectedChannelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [channel, dispatch] = useReducer(selectedChannelReducer(), initialChannelState);
    const history = useContext(ChannelHistoryContext);

    const select = useCallback((m3uMedia: M3uMedia) => {
        dispatch(selectChannel(m3uMedia));
        history.addToHistory(m3uMedia);
    }, [history]);

    const remove = useCallback(() => {
        dispatch(removeSelectedChannel());
    }, []);

    const goBack = useCallback(() => {
        const prevChannel = history.goBack();
        if (prevChannel) {
            dispatch(selectChannel(prevChannel));
        }
    }, [history]);

    const goForward = useCallback(() => {
        const nextChannel = history.goForward();
        if (nextChannel) {
            dispatch(selectChannel(nextChannel));
        }
    }, [history]);

    const contextValue = useMemo(() => ({
        data: channel.data,
        selectChannel: select,
        removeSelectedChannel: remove,
        canGoBack: history.canGoBack,
        canGoForward: history.canGoForward,
        goBack,
        goForward,
    }), [channel.data, select, remove, history.canGoBack, history.canGoForward, goBack, goForward]);

    return (
        <SelectedChannelContext.Provider value={contextValue}>
            {children}
        </SelectedChannelContext.Provider>
    );
};

export default SelectedChannelProvider;